import React, { useCallback, useState } from 'react'
import moment from 'moment'
import { FaDownload } from 'react-icons/fa'
import DataTableForCustomPaginationRoute from '../../../../components/DataTableForCustomPaginationRoute'
import Container from '../../../../components/Container'
import { Alert } from '../../../../components/Alert'
import api from '../../../../services/api'
import { INPUT_MASK } from '../../../../common/constants'
import { useToast } from '../../../../hooks/toast'
import { useUpdateDataTable } from '../../../../hooks/dataTable'
import { getFileNameWithOutHash } from '../../../../utils/getFileNameWithOutHash'
import { nameSource } from '../domain/info'
import { headers } from '../domain/headers'
import { breadcrumbList } from '../domain/breadcrumb'
import { toolsList } from '../domain/tools'
import { apiDelete, apiListPagination } from '../domain/api'

const truncateText = (text: string, maxLength = 100): string => {
  if (text.length <= maxLength) {
    return text
  }

  return `${text.slice(0, maxLength)}...`
}

const AdvertisementsList: React.FC = () => {
  const { addToast } = useToast()
  const { updateDataTable } = useUpdateDataTable()
  const [alertRemove, setAlertRemove] = useState(false)
  const [itemToRemove, setItemToRemove] = useState<number | null>(null)

  const handleOpenRemoveAlert = useCallback(
    (item: AdvertisementsPaginationItem) => {
      setItemToRemove(item.id)
      setAlertRemove(true)
    },
    []
  )

  const handleConfirmRemove = useCallback(async () => {
    if (!itemToRemove) {
      return
    }

    try {
      await api.delete(apiDelete(String(itemToRemove)))
      setAlertRemove(false)
      setItemToRemove(null)
      addToast({
        type: 'success',
        title: 'Anúncio removido com sucesso.'
      })
      updateDataTable()
    } catch (err: any) {
      setAlertRemove(false)
      if (err.response?.status === 403) {
        addToast({
          type: 'error',
          title: err.response.data.message
        })
        return
      }
      addToast({
        type: 'error',
        title: 'Anúncio não removido.'
      })
    }
  }, [addToast, itemToRemove, updateDataTable])

  const handleDownloadImage = useCallback(
    async (item: AdvertisementsPaginationItem) => {
      if (!item.image_url) {
        return
      }

      try {
        const response = await fetch(item.image_url)
        const blob = await response.blob()
        const urlSegment = item.image_url.split('/').pop() || ''
        const fileName =
          getFileNameWithOutHash(urlSegment) || `anuncio-${item.id}.jpg`
        const blobURL = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobURL
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobURL)
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao baixar imagem',
          description: 'Não foi possível baixar a imagem. Tente novamente.'
        })
      }
    },
    [addToast]
  )

  return (
    <>
      <Container
        pageTitle="Listagem"
        breadcrumb={breadcrumbList}
        tools={toolsList}
      >
        <DataTableForCustomPaginationRoute
          paginationUrl={apiListPagination()}
          source={nameSource}
          notHasChildren
          headers={headers}
          customHeaders={[
            {
              name: 'Descrição',
              field: 'description',
              sortable: false,
              element: item => (
                <p>{item.description ? truncateText(item.description) : '-'}</p>
              )
            },
            {
              name: 'Link',
              field: 'link',
              sortable: false,
              element: item =>
                item.link ? (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.link}
                  >
                    {truncateText(item.link, 50)}
                  </a>
                ) : (
                  <span>-</span>
                )
            },
            {
              name: 'Criado em',
              field: 'created_at',
              sortable: false,
              element: item => (
                <p>
                  {moment(item?.created_at).format(
                    INPUT_MASK.DATE_LOCALE_WITH_TIME
                  )}
                </p>
              )
            },
            {
              name: 'Ativo',
              field: 'active',
              sortable: true,
              element: (item: { active: string }) => (
                <p>{item.active ? 'Sim' : 'Não'}</p>
              )
            }
          ]}
          actions={[
            {
              name: 'view',
              title: 'Visualizar',
              linkTo: (item: AdvertisementsPaginationItem) =>
                `/commercial/advertisements/view/${item.id}`,
              htmlIcon: <span className="fa fa-search" />
            },
            {
              name: 'edit',
              title: 'Editar',
              linkTo: (item: AdvertisementsPaginationItem) =>
                `/commercial/advertisements/update/${item.id}`,
              htmlIcon: <span className="fa fa-edit" />
            },
            {
              name: 'download_image',
              title: 'Baixar imagem',
              onClick: (item: AdvertisementsPaginationItem) => {
                if (!item.image_url) {
                  return
                }

                handleDownloadImage(item)
              },
              element: (item: AdvertisementsPaginationItem) => (
                <span
                  title={
                    item.image_url ? 'Baixar imagem' : 'Sem imagem disponível'
                  }
                  style={{
                    display: 'inline-flex',
                    opacity: item.image_url ? 1 : 0.35,
                    cursor: item.image_url ? 'pointer' : 'not-allowed'
                  }}
                >
                  <FaDownload size={16} />
                </span>
              )
            },
            {
              name: 'remove',
              title: 'Remover',
              onClick: (item: AdvertisementsPaginationItem) => {
                handleOpenRemoveAlert(item)
              },
              htmlIcon: <span className="fa fa-remove" />
            }
          ]}
        />
      </Container>

      <Alert
        message={`Tem certeza que deseja excluir o registro ${itemToRemove} ?`}
        onClickCancellButton={() => {
          setAlertRemove(false)
          setItemToRemove(null)
        }}
        onClickConfirmButton={() => handleConfirmRemove()}
        isActive={alertRemove}
      />
    </>
  )
}

export default AdvertisementsList
